import { FC } from "react";

import Box from "@mui/material/Box";
import { browserDefaultTimezone } from "@simplyk/common";

import { resolveScheduleDates } from "../../../helpers/resolveScheduleDates";
import { useRateScheduleDigest } from "../../../RatesDigest/RatesDigestContext";
import { RateData } from "../../../types";

import { BULLET_SEPARATOR } from "./helpers";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { buildScheduleSummaryLabel } from "@/common/helpers/scheduleSummaryLabel";
import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardIcon } from "@/dashboard-design-system/Icon/DashboardIcon";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { useStableLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { Date as DateIcon } from "@/icons/outlined";

import { summaryToneVibes } from "../ExtrasSummary/summaryTone";

interface ScheduleSummarySegmentProps {
  rate: Partial<RateData>;
  withSeparator?: boolean;
}

export const ScheduleSummarySegment: FC<ScheduleSummarySegmentProps> = ({ rate, withSeparator }) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { apiFormObject: apiTicketing } = useStableLiveFormEditorContext<"ticketing">();
  const allRates = useRateScheduleDigest();

  const resolvedSchedule = rate.isScheduled
    ? resolveScheduleDates(
        {
          isScheduled: rate.isScheduled,
          salesStartMode: rate.salesStartMode ?? "formDefault",
          salesStartDate: rate.salesStartDate ?? null,
          salesStartAfterRateId: rate.salesStartAfterRateId ?? null,
          salesEndMode: rate.salesEndMode ?? "formDefault",
          salesEndDate: rate.salesEndDate ?? null,
        },
        allRates
      )
    : null;
  const part = resolvedSchedule
    ? buildScheduleSummaryLabel({
        startDate: resolvedSchedule.startDate,
        endDate: resolvedSchedule.endDate,
        t,
        locale,
        timezone: apiTicketing.eventTimezone || browserDefaultTimezone,
        now: new Date(),
      })
    : null;

  if (!part || part.tone === "danger") {
    return null;
  }

  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
      {withSeparator && (
        <DashboardTypography
          component="span"
          variant="body2"
          sx={(theme) => ({ color: theme.palette.text.neutral.quiet, whiteSpace: "pre", flexShrink: 0 })}
        >
          {BULLET_SEPARATOR}
        </DashboardTypography>
      )}
      <Box component="span" sx={{ display: "inline-flex", flexShrink: 0 }}>
        <DashboardIcon size="small" vibe={summaryToneVibes[part.tone]}>
          <DateIcon />
        </DashboardIcon>
      </Box>
      <DashboardTypography
        component="span"
        variant="body2"
        vibe={summaryToneVibes[part.tone]}
        sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
      >
        {part.text}
      </DashboardTypography>
    </Box>
  );
};
